import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AboutCoreDiagram from './AboutCoreDiagram';
import FloatingShapes from '@/components/FloatingShapes';

const PILLARS = [
  {
    icon: 'ri-brain-line',
    labelKey: 'about.who.p1.label',
    textKey: 'about.who.p1.text',
  },
  {
    icon: 'ri-code-s-slash-line',
    labelKey: 'about.who.p2.label',
    textKey: 'about.who.p2.text',
  },
  {
    icon: 'ri-database-2-line',
    labelKey: 'about.who.p3.label',
    textKey: 'about.who.p3.text',
  },
  {
    icon: 'ri-settings-4-line',
    labelKey: 'about.who.p4.label',
    textKey: 'about.who.p4.text',
  },
];

// Posición de cada tarjeta en pantallas grandes: 1ª y 2ª a la izquierda,
// 3ª y 4ª a la derecha del diagrama. En móvil se acomodan solas.
const PILLAR_PLACEMENT = [
  'lg:col-start-1 lg:row-start-1',
  'lg:col-start-1 lg:row-start-2',
  'lg:col-start-4 lg:row-start-1',
  'lg:col-start-4 lg:row-start-2',
];

export default function AboutWhoWeAreSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-14 md:py-16 bg-background-50 overflow-hidden">
      <FloatingShapes count={12} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={8} color="secondary" speed={0.5} />
      </div>

      <div className="absolute top-0 left-0 w-72 h-72 bg-accent-500/5 rounded-full blur-3xl animate-[morphFloat_8s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary-500/6 rounded-full blur-3xl animate-[morphPulse_7s_ease-in-out_infinite_1s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-10 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-[10px] font-mono font-medium text-accent-700 uppercase tracking-widest mb-4">
              {t('about.who.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('about.who.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mt-4">
              {t('about.who.paragraph')}
            </p>
          </div>

          {/* Diagrama central con dos tarjetas a cada lado */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-6 items-center">
            {/* Animated diagram — centro */}
            <div
              className={`col-span-2 lg:col-start-2 lg:col-span-2 lg:row-start-1 lg:row-span-2 mx-auto w-full max-w-xl transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '150ms' }}
            >
              <div className="rounded-2xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm p-4 md:p-6">
                <AboutCoreDiagram />
              </div>
            </div>

            {/* Four pillars — dos por lado */}
            {PILLARS.map((pillar, i) => (
              <div
                key={pillar.labelKey}
                className={`${PILLAR_PLACEMENT[i]} relative p-5 md:p-6 rounded-xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm transition-all duration-700 hover:-translate-y-1 hover:border-accent-200/70 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${250 + i * 120}ms` }}
              >
                <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center mb-4">
                  <i className={`${pillar.icon} text-xl text-secondary-700`} />
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                  {t(pillar.labelKey)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(pillar.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}